import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { 
  FiX, FiShield, FiFileText, FiHelpCircle, FiMail, FiPhone, 
  FiMapPin, FiClock, FiMessageCircle, FiExternalLink,
  FiLock, FiEye, FiUsers, FiServer, FiAlertTriangle
} = FiIcons;

function FooterModals({ isOpen, modalType, onClose }) {
  const modalContent = {
    privacy: {
      title: "Privacy Policy",
      icon: FiShield,
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <p className="text-sm text-blue-800 dark:text-blue-300">
              <strong>Last Updated:</strong> January 15, 2025
            </p>
          </div>

          <section>
            <h3 className="text-lg font-semibold text-government-900 dark:text-white mb-3 flex items-center">
              <SafeIcon icon={FiEye} className="w-5 h-5 mr-2 text-primary-500" />
              Information We Collect
            </h3>
            <div className="space-y-3 text-sm text-government-600 dark:text-government-300">
              <div>
                <h4 className="font-medium text-government-800 dark:text-government-200 mb-1">Personal Information:</h4>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Name, email address, and contact details</li>
                  <li>Prison visiting details and prisoner information</li>
                  <li>Payment information (securely processed by third parties)</li>
                  <li>Order history and preferences</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-government-800 dark:text-government-200 mb-1">Technical Information:</h4>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>IP address and browser information</li>
                  <li>Usage patterns and system interactions</li>
                  <li>Security logs and access records</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-government-900 dark:text-white mb-3 flex items-center">
              <SafeIcon icon={FiLock} className="w-5 h-5 mr-2 text-primary-500" />
              How We Use Your Information
            </h3>
            <ul className="list-disc list-inside space-y-2 text-sm text-government-600 dark:text-government-300 ml-4">
              <li>Processing and fulfilling your food orders</li>
              <li>Facilitating secure payment transactions</li>
              <li>Communicating about your orders and visits</li>
              <li>Improving our services and user experience</li>
              <li>Ensuring system security and preventing fraud</li>
              <li>Complying with legal and regulatory requirements</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-government-900 dark:text-white mb-3 flex items-center">
              <SafeIcon icon={FiUsers} className="w-5 h-5 mr-2 text-primary-500" />
              Information Sharing
            </h3>
            <div className="space-y-3 text-sm text-government-600 dark:text-government-300">
              <p>We share your information only as necessary:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><strong>Prison Facilities:</strong> Order details for fulfillment</li>
                <li><strong>Payment Processors:</strong> Secure transaction processing</li>
                <li><strong>Legal Authorities:</strong> When required by law</li>
                <li><strong>Service Providers:</strong> Technical support and maintenance</li>
              </ul>
              <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                <p className="text-green-800 dark:text-green-300 text-xs">
                  <strong>We never sell or rent your personal information to third parties.</strong>
                </p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-government-900 dark:text-white mb-3 flex items-center">
              <SafeIcon icon={FiServer} className="w-5 h-5 mr-2 text-primary-500" />
              Data Security
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="bg-government-50 dark:bg-government-800 p-3 rounded-lg">
                <h4 className="font-medium text-government-800 dark:text-government-200 mb-2">Encryption</h4>
                <p className="text-government-600 dark:text-government-300">256-bit SSL encryption for all data transmission and storage</p>
              </div>
              <div className="bg-government-50 dark:bg-government-800 p-3 rounded-lg">
                <h4 className="font-medium text-government-800 dark:text-government-200 mb-2">Access Controls</h4>
                <p className="text-government-600 dark:text-government-300">Multi-factor authentication and role-based permissions</p>
              </div>
              <div className="bg-government-50 dark:bg-government-800 p-3 rounded-lg">
                <h4 className="font-medium text-government-800 dark:text-government-200 mb-2">Monitoring</h4>
                <p className="text-government-600 dark:text-government-300">24/7 security monitoring and threat detection</p>
              </div>
              <div className="bg-government-50 dark:bg-government-800 p-3 rounded-lg">
                <h4 className="font-medium text-government-800 dark:text-government-200 mb-2">Compliance</h4>
                <p className="text-government-600 dark:text-government-300">GDPR, PCI DSS, and UK government standards</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-government-900 dark:text-white mb-3">Your Rights</h3>
            <div className="space-y-2 text-sm text-government-600 dark:text-government-300">
              <p>Under GDPR and UK data protection law, you have the right to:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Access your personal data</li>
                <li>Correct inaccurate information</li>
                <li>Delete your data (subject to legal requirements)</li>
                <li>Restrict processing of your data</li>
                <li>Data portability</li>
                <li>Object to processing</li>
              </ul>
            </div>
          </section>

          <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
            <p className="text-sm text-yellow-800 dark:text-yellow-300">
              <strong>Questions about your privacy?</strong> Contact our Data Protection Officer at privacy@ab-orders.com or call 0800-AB-PRIVACY.
            </p>
          </div>
        </div>
      )
    },

    terms: {
      title: "Terms of Service",
      icon: FiFileText,
      content: (
        <div className="space-y-6">
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
            <p className="text-sm text-red-800 dark:text-red-300">
              <strong>Effective Date:</strong> January 15, 2025 | <strong>Version:</strong> 2.1
            </p>
          </div>

          <section>
            <h3 className="text-lg font-semibold text-government-900 dark:text-white mb-3">1. Acceptance of Terms</h3>
            <p className="text-sm text-government-600 dark:text-government-300">
              By accessing and using AB Orders, you accept and agree to be bound by these Terms of Service. 
              If you do not agree to these terms, you may not use our service.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-government-900 dark:text-white mb-3">2. Service Description</h3>
            <div className="space-y-3 text-sm text-government-600 dark:text-government-300">
              <p>AB Orders provides a pre-ordering system for prison visits, allowing:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Online food and beverage ordering for prison visits</li>
                <li>Secure payment processing</li>
                <li>Order management and tracking</li>
                <li>Collection coordination with participating prisons</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-government-900 dark:text-white mb-3">3. User Responsibilities</h3>
            <div className="space-y-3 text-sm text-government-600 dark:text-government-300">
              <h4 className="font-medium text-government-800 dark:text-government-200">Account Security:</h4>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Maintain confidentiality of login credentials</li>
                <li>Notify us immediately of unauthorized access</li>
                <li>Provide accurate and current information</li>
              </ul>
              
              <h4 className="font-medium text-government-800 dark:text-government-200 mt-3">Ordering Guidelines:</h4>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Orders must be placed at least 24 hours before visit</li>
                <li>Provide accurate visiting details and prisoner information</li>
                <li>Arrive within specified collection timeframes</li>
                <li>Follow prison security protocols and regulations</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-government-900 dark:text-white mb-3">4. Payment Terms</h3>
            <div className="space-y-3 text-sm text-government-600 dark:text-government-300">
              <div className="bg-government-50 dark:bg-government-800 p-3 rounded-lg">
                <h4 className="font-medium text-government-800 dark:text-government-200 mb-2">Payment Processing:</h4>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>All payments processed securely through approved gateways</li>
                  <li>Prices include VAT where applicable</li>
                  <li>Payment required at time of order placement</li>
                </ul>
              </div>
              
              <div className="bg-government-50 dark:bg-government-800 p-3 rounded-lg">
                <h4 className="font-medium text-government-800 dark:text-government-200 mb-2">Refund Policy:</h4>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Full refunds: Up to 48 hours before visit</li>
                  <li>Partial refunds: Up to 24 hours before visit</li>
                  <li>Emergency refunds: At discretion for documented emergencies</li>
                  <li>Prison-cancelled visits: Automatic full refund</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-government-900 dark:text-white mb-3">5. Service Limitations</h3>
            <div className="space-y-2 text-sm text-government-600 dark:text-government-300">
              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg">
                <h4 className="font-medium text-yellow-800 dark:text-yellow-200 mb-1 flex items-center">
                  <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 mr-1" />
                  Important Limitations:
                </h4>
                <ul className="list-disc list-inside space-y-1 ml-4 text-yellow-700 dark:text-yellow-300">
                  <li>Service availability subject to prison policies</li>
                  <li>Menu items subject to stock availability</li>
                  <li>Orders may be cancelled due to security requirements</li>
                  <li>System maintenance may temporarily limit access</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-government-900 dark:text-white mb-3">6. Prohibited Uses</h3>
            <div className="space-y-2 text-sm text-government-600 dark:text-government-300">
              <p>You agree not to:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Use the service for illegal or unauthorized purposes</li>
                <li>Attempt to gain unauthorized access to system components</li>
                <li>Interfere with service operation or other users</li>
                <li>Provide false or misleading information</li>
                <li>Violate any prison rules or security protocols</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-government-900 dark:text-white mb-3">7. Limitation of Liability</h3>
            <p className="text-sm text-government-600 dark:text-government-300">
              AB Orders provides services "as is" and disclaims warranties to the fullest extent permitted by law. 
              Our liability is limited to the amount paid for services in the event of any claims.
            </p>
          </section>

          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <p className="text-sm text-blue-800 dark:text-blue-300">
              <strong>Questions about these terms?</strong> Contact our legal team at legal@ab-orders.com
            </p>
          </div>
        </div>
      )
    },

    support: {
      title: "Support Center",
      icon: FiHelpCircle,
      content: (
        <div className="space-y-6">
          <div className="text-center">
            <h3 className="text-xl font-bold text-government-900 dark:text-white mb-2">How Can We Help You?</h3>
            <p className="text-government-600 dark:text-government-300">
              Our support team is here 24/7 to assist with any questions about AB Orders
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <h4 className="font-semibold text-blue-800 dark:text-blue-300 mb-2 flex items-center">
                <SafeIcon icon={FiMessageCircle} className="w-5 h-5 mr-2" />
                Live Chat Support
              </h4>
              <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
                Get instant help from our support specialists
              </p>
              <div className="text-xs text-blue-600 dark:text-blue-400">
                <p><strong>Available:</strong> 24/7</p>
                <p><strong>Response Time:</strong> Under 2 minutes</p>
              </div>
            </div>

            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
              <h4 className="font-semibold text-green-800 dark:text-green-300 mb-2 flex items-center">
                <SafeIcon icon={FiPhone} className="w-5 h-5 mr-2" />
                Phone Support
              </h4>
              <p className="text-sm text-green-700 dark:text-green-300 mb-3">
                Speak directly with our support team
              </p>
              <div className="text-xs text-green-600 dark:text-green-400">
                <p><strong>UK:</strong> 0800-AB-ORDERS</p>
                <p><strong>Hours:</strong> 24/7 Emergency Line</p>
              </div>
            </div>
          </div>

          <section>
            <h3 className="text-lg font-semibold text-government-900 dark:text-white mb-4">Common Issues & Solutions</h3>
            
            <div className="space-y-4">
              <div className="border border-government-200 dark:border-government-600 rounded-lg p-4">
                <h4 className="font-medium text-government-800 dark:text-government-200 mb-2">🔐 Login Problems</h4>
                <ul className="text-sm text-government-600 dark:text-government-300 space-y-1">
                  <li>• Check email and password spelling</li>
                  <li>• Clear browser cache and cookies</li>
                  <li>• Use "Forgot Password" feature</li>
                  <li>• Try a different browser or device</li>
                </ul>
              </div>

              <div className="border border-government-200 dark:border-government-600 rounded-lg p-4">
                <h4 className="font-medium text-government-800 dark:text-government-200 mb-2">💳 Payment Issues</h4>
                <ul className="text-sm text-government-600 dark:text-government-300 space-y-1">
                  <li>• Verify card details and billing address</li>
                  <li>• Check daily/monthly spending limits</li>
                  <li>• Ensure sufficient funds available</li>
                  <li>• Contact your bank if card is blocked</li>
                </ul>
              </div>

              <div className="border border-government-200 dark:border-government-600 rounded-lg p-4">
                <h4 className="font-medium text-government-800 dark:text-government-200 mb-2">📦 Order Problems</h4>
                <ul className="text-sm text-government-600 dark:text-government-300 space-y-1">
                  <li>• Check order confirmation email</li>
                  <li>• Verify visit date and time</li>
                  <li>• Contact prison reception if order not found</li>
                  <li>• Keep order number for reference</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-government-900 dark:text-white mb-3">Emergency Support</h3>
            <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
              <h4 className="font-medium text-red-800 dark:text-red-300 mb-2">Urgent Issues on Visit Day:</h4>
              <div className="space-y-2 text-sm text-red-700 dark:text-red-300">
                <p>📞 <strong>Emergency Line:</strong> 0800-URGENT-AB</p>
                <p>💬 <strong>Priority Chat:</strong> Use "Emergency" option in chat</p>
                <p>📧 <strong>Emergency Email:</strong> emergency@ab-orders.com</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-government-900 dark:text-white mb-3">Self-Service Options</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button className="p-3 text-left bg-government-50 dark:bg-government-800 rounded-lg hover:bg-government-100 dark:hover:bg-government-700 transition-colors">
                <h4 className="font-medium text-government-800 dark:text-government-200">📚 Help Center</h4>
                <p className="text-xs text-government-600 dark:text-government-300">Comprehensive guides and tutorials</p>
              </button>
              
              <button className="p-3 text-left bg-government-50 dark:bg-government-800 rounded-lg hover:bg-government-100 dark:hover:bg-government-700 transition-colors">
                <h4 className="font-medium text-government-800 dark:text-government-200">❓ FAQ</h4>
                <p className="text-xs text-government-600 dark:text-government-300">Answers to common questions</p>
              </button>
              
              <button className="p-3 text-left bg-government-50 dark:bg-government-800 rounded-lg hover:bg-government-100 dark:hover:bg-government-700 transition-colors">
                <h4 className="font-medium text-government-800 dark:text-government-200">🎥 Video Guides</h4>
                <p className="text-xs text-government-600 dark:text-government-300">Step-by-step video tutorials</p>
              </button>
              
              <button className="p-3 text-left bg-government-50 dark:bg-government-800 rounded-lg hover:bg-government-100 dark:hover:bg-government-700 transition-colors">
                <h4 className="font-medium text-government-800 dark:text-government-200">📋 System Status</h4>
                <p className="text-xs text-government-600 dark:text-government-300">Real-time service status</p>
              </button>
            </div>
          </section>

          <div className="bg-primary-50 dark:bg-primary-900/20 p-4 rounded-lg text-center">
            <p className="text-sm text-primary-800 dark:text-primary-300">
              <strong>Still need help?</strong> Our support team responds to all inquiries within 15 minutes during business hours.
            </p>
          </div>
        </div>
      )
    },

    contact: {
      title: "Contact Us",
      icon: FiMail,
      content: (
        <div className="space-y-6">
          <div className="text-center">
            <h3 className="text-xl font-bold text-government-900 dark:text-white mb-2">Get in Touch</h3>
            <p className="text-government-600 dark:text-government-300">
              We're here to help with any questions or concerns about AB Orders
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Contact Methods */}
            <div className="space-y-4">
              <h4 className="font-semibold text-government-900 dark:text-white">Contact Methods</h4>
              
              <div className="space-y-3">
                <div className="flex items-start space-x-3 p-3 bg-government-50 dark:bg-government-800 rounded-lg">
                  <SafeIcon icon={FiPhone} className="w-5 h-5 text-primary-500 mt-0.5" />
                  <div>
                    <h5 className="font-medium text-government-800 dark:text-government-200">Phone Support</h5>
                    <p className="text-sm text-government-600 dark:text-government-300">0800-AB-ORDERS (0800-226-7337)</p>
                    <p className="text-xs text-government-500 dark:text-government-400">24/7 Support Available</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-3 bg-government-50 dark:bg-government-800 rounded-lg">
                  <SafeIcon icon={FiMail} className="w-5 h-5 text-primary-500 mt-0.5" />
                  <div>
                    <h5 className="font-medium text-government-800 dark:text-government-200">Email Support</h5>
                    <p className="text-sm text-government-600 dark:text-government-300">support@ab-orders.com</p>
                    <p className="text-xs text-government-500 dark:text-government-400">Response within 2 hours</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-3 bg-government-50 dark:bg-government-800 rounded-lg">
                  <SafeIcon icon={FiMessageCircle} className="w-5 h-5 text-primary-500 mt-0.5" />
                  <div>
                    <h5 className="font-medium text-government-800 dark:text-government-200">Live Chat</h5>
                    <p className="text-sm text-government-600 dark:text-government-300">Available on all pages</p>
                    <p className="text-xs text-government-500 dark:text-government-400">Instant response 24/7</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Office Information */}
            <div className="space-y-4">
              <h4 className="font-semibold text-government-900 dark:text-white">Office Locations</h4>
              
              <div className="space-y-4">
                <div className="p-4 border border-government-200 dark:border-government-600 rounded-lg">
                  <h5 className="font-medium text-government-800 dark:text-government-200 mb-2 flex items-center">
                    <SafeIcon icon={FiMapPin} className="w-4 h-4 mr-2 text-primary-500" />
                    Head Office - London
                  </h5>
                  <address className="text-sm text-government-600 dark:text-government-300 not-italic">
                    AB Orders Ltd<br/>
                    25 Victoria Street<br/>
                    Westminster, London SW1H 0EX<br/>
                    United Kingdom
                  </address>
                </div>

                <div className="p-4 border border-government-200 dark:border-government-600 rounded-lg">
                  <h5 className="font-medium text-government-800 dark:text-government-200 mb-2 flex items-center">
                    <SafeIcon icon={FiClock} className="w-4 h-4 mr-2 text-primary-500" />
                    Business Hours
                  </h5>
                  <div className="text-sm text-government-600 dark:text-government-300">
                    <p><strong>Support:</strong> 24/7</p>
                    <p><strong>Office:</strong> Mon-Fri, 9am-6pm GMT</p>
                    <p><strong>Emergency:</strong> 24/7</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <section>
            <h4 className="font-semibold text-government-900 dark:text-white mb-3">Department-Specific Contacts</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <h5 className="font-medium text-blue-800 dark:text-blue-300">General Support</h5>
                <p className="text-sm text-blue-700 dark:text-blue-300">support@ab-orders.com</p>
              </div>
              
              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <h5 className="font-medium text-green-800 dark:text-green-300">Technical Issues</h5>
                <p className="text-sm text-green-700 dark:text-green-300">tech@ab-orders.com</p>
              </div>
              
              <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <h5 className="font-medium text-purple-800 dark:text-purple-300">Billing & Payments</h5>
                <p className="text-sm text-purple-700 dark:text-purple-300">billing@ab-orders.com</p>
              </div>
              
              <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                <h5 className="font-medium text-orange-800 dark:text-orange-300">Prison Partnerships</h5>
                <p className="text-sm text-orange-700 dark:text-orange-300">partnerships@ab-orders.com</p>
              </div>
              
              <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <h5 className="font-medium text-red-800 dark:text-red-300">Security & Privacy</h5>
                <p className="text-sm text-red-700 dark:text-red-300">security@ab-orders.com</p>
              </div>
              
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <h5 className="font-medium text-yellow-800 dark:text-yellow-300">Media & Press</h5>
                <p className="text-sm text-yellow-700 dark:text-yellow-300">press@ab-orders.com</p>
              </div>
            </div>
          </section>

          <section>
            <h4 className="font-semibold text-government-900 dark:text-white mb-3">Quick Contact Form</h4>
            <form className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Your Name"
                  className="px-4 py-2 border border-government-300 dark:border-government-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-government-700 text-government-900 dark:text-white"
                />
                <input
                  type="email"
                  placeholder="Your Email"
                  className="px-4 py-2 border border-government-300 dark:border-government-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-government-700 text-government-900 dark:text-white"
                />
              </div>
              
              <select className="w-full px-4 py-2 border border-government-300 dark:border-government-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-government-700 text-government-900 dark:text-white">
                <option>Select Department</option>
                <option>General Support</option>
                <option>Technical Issues</option>
                <option>Billing & Payments</option>
                <option>Prison Partnerships</option>
                <option>Security & Privacy</option>
              </select>
              
              <textarea
                rows="4"
                placeholder="How can we help you?"
                className="w-full px-4 py-2 border border-government-300 dark:border-government-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-government-700 text-government-900 dark:text-white resize-none"
              ></textarea>
              
              <button
                type="submit"
                className="w-full bg-primary-500 hover:bg-primary-600 text-white py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiMail} className="w-4 h-4" />
                <span>Send Message</span>
              </button>
            </form>
          </section>

          <div className="bg-government-50 dark:bg-government-800 p-4 rounded-lg">
            <p className="text-sm text-government-600 dark:text-government-300 text-center">
              <strong>Need immediate assistance?</strong> Call our 24/7 support line at 0800-AB-ORDERS or use the live chat feature.
            </p>
          </div>
        </div>
      )
    }
  };

  const currentModal = modalContent[modalType];

  return (
    <AnimatePresence>
      {isOpen && currentModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white dark:bg-government-900 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-primary-500 to-primary-600 p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <SafeIcon icon={currentModal.icon} className="w-5 h-5" />
                  </div>
                  <h2 className="text-2xl font-bold">{currentModal.title}</h2>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {currentModal.content}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default FooterModals;